import React from 'react';
import styled, { css } from 'styled-components';
import { media, colors, typography } from '../../../shared/lib/styles';
import { designConsultationPath } from '../../../shared/lib/platformUrls';
import Heading from '../../../shared/components/Heading';
import { ChameleonCTALink } from '../../../shared/components/Link';
import Contacts1 from '../../../shared/components/Icon/components/Contacts1';
import Phone1 from '../../../shared/components/Icon/components/Phone1';
import Columns, { Column } from '../../../shared/components/Columns';
import PhoneNumber from '../../../shared/components/PhoneNumber';
import SupportPerson from './SupportPerson';
import Inline from '../../../shared/spa/core-components/Inline';
import Stack from '../../../shared/spa/core-components/Stack';
import Box from '../../../shared/spa/core-components/Box';

export interface Props {
    supportPhoneNumber: string;
    featureColor?: string;
    description: React.ReactNode;
    heading: string;
}

const defaultFeatureColor = '#952F38';

const StyledColumn = styled(Column)`
    &:first-of-type {
        display: flex;
        justify-content: flex-end;

        @media ${media('small')} {
            justify-content: center;
        }
    }
`;

const NarrowContactWrapper = styled(Box)`
    max-width: 604px;
    background: linear-gradient(to top, ${colors.white}, ${colors.grey100} 75%, transparent 75%);

    @media ${media('small')} {
        background: none;
    }
`;

const ContactDetailsWrapper = styled.div<{ color?: string }>(
    ({ color }) => css`
        color: ${color ? color : defaultFeatureColor};
    `
);

// TODO: Figure out if that's share-worthy
const NarrowText = styled.div`
    max-width: 20em;

    @media ${media('small')} {
        max-width: none;
    }
`;

const Description = styled(NarrowText)`
    color: ${colors.typography.root};
`;

const PhoneIcon = styled(Phone1)`
    font-size: ${typography.fontSizes.links.jumbo}px;
`;

const ContactIcon = styled(Contacts1)`
    font-size: ${typography.fontSizes.links.jumbo}px;
`;

const ContactDetails = ({ supportPhoneNumber, featureColor, description, heading }: Props) => (
    <ContactDetailsWrapper color={featureColor}>
        <Heading type="h2" variations={['h2', 'h2-capped', 'with-divider', 'no-margin']}>
            {heading}
        </Heading>

        <Box paddingLeft="space10" paddingTop="space5">
            <Stack space="space10">
                <Description>{description}</Description>

                <Stack space="space3">
                    <Inline space="space6">
                        <PhoneIcon />
                        <PhoneNumber phoneNumber={supportPhoneNumber} />
                    </Inline>

                    <Inline space="space6">
                        <ContactIcon />
                        <ChameleonCTALink href={designConsultationPath()}>
                            {__('Free design consultation')}
                        </ChameleonCTALink>
                    </Inline>
                </Stack>
            </Stack>
        </Box>
    </ContactDetailsWrapper>
);

const Contact = ({ ...props }: Props) => (
    <Columns>
        <StyledColumn width={[12, 6, 6, 6]}>
            <SupportPerson />
        </StyledColumn>

        <StyledColumn width={[12, 6, 6, 6]}>
            <ContactDetails {...props} />
        </StyledColumn>
    </Columns>
);

export const NarrowContact = ({ ...props }: Props) => (
    <NarrowContactWrapper
        paddingLeft={['none', 'space14', 'space14']}
        paddingRight={['none', 'space14', 'space14']}
        paddingTop="space4"
    >
        <Box display="flex" justifyContent="center">
            <SupportPerson />
        </Box>
        <ContactDetails {...props} />
    </NarrowContactWrapper>
);

export default Contact;
